import React, { useState } from "react";
import { formatDateFromMillis } from "../../utils/dateUtils";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { Roles } from "../../constants/roles";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";
import ModelGroupingModal from "../modals/ModelGroupingModal";

const ModelFiles = ({
  modelInfos,
  handleDownload,
  handleDelete,
  handleSaveModelGroups,
  modelGroups = {},
  currentUserId,
  isMobile,
  userRole,
  isDemo = false,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFileIndex, setSelectedFileIndex] = useState(null);
  const [showGroupingModal, setShowGroupingModal] = useState(false);
  const [editingGroupType, setEditingGroupType] = useState(null); // 'personal', 'common', or null for new

  const isViewer = userRole === Roles.VIEWER;
  const canDelete = !isViewer && !isDemo;

  const confirmDelete = (index) => {
    setSelectedFileIndex(index);
    setShowDeleteModal(true);
  };
  const handleConfirmDelete = () => {
    if (selectedFileIndex !== null) {
      handleDelete(selectedFileIndex);
      setShowDeleteModal(false);
      setSelectedFileIndex(null);
    }
  };
  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setSelectedFileIndex(null);
  };

  const handleSaveGroups = async (groups, applyToAllUsers) => {
    try {
      await handleSaveModelGroups(groups, applyToAllUsers);
      setShowGroupingModal(false);
      setEditingGroupType(null);
    } catch (error) {
      console.error("Error saving groups:", error);
      alert("Failed to save groups. Please try again.");
    }
  };

  const handleOpenGroupingModal = (groupType = null) => {
    setEditingGroupType(groupType);
    setShowGroupingModal(true);
  };

  // Get the appropriate groups based on editing type
  const getExistingGroups = () => {
    if (editingGroupType === "personal") {
      return modelGroups[currentUserId] || [];
    } else if (editingGroupType === "common") {
      return modelGroups["common"] || [];
    }
    return [];
  };

  // Check if groups exist
  const hasPersonalGroup =
    modelGroups[currentUserId] && modelGroups[currentUserId].length > 0;
  const hasCommonGroup =
    modelGroups["common"] && modelGroups["common"].length > 0;

  // Check if any model has a thumbnail
  const hasAnyThumbnail = modelInfos?.some((file) => file?.thumbnailUrl);

  const labelCls = "text-[13px] font-[700] mr-1";
  const labelStyle = { color: "#475569" };
  const valueCls = "text-[13px] font-[400]";
  const valueStyle = { color: "#0F172A" };
  const ThumbnailImage = ({ file, className = "" }) => (
    <div
      className={`w-12 h-12 rounded-md flex items-center justify-center overflow-hidden select-none mx-auto ${className}`}
      style={{ backgroundColor: "#F1F5F9" }}
    >
      {file?.thumbnailUrl ? (
        <img
          src={file.thumbnailUrl}
          alt={`${file.name} thumbnail`}
          className="w-full h-full object-cover rounded"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <span className="text-[11px] leading-3 text-center" style={{ color: "#64748B" }}>No<br />Image</span>
      )}
    </div>
  );

  // ——— MOBILE (yeni kart tasarım) ———
  if (isMobile) {
    return (
      <div className="rounded-xl p-5 md:p-6" style={{ backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        {/* Başlık kart içinde */}
        <div className="flex items-center justify-between">
          <h2 className="text-[20px] md:text-[22px] font-[800]" style={{ color: "#0F172A" }}>
            Model Files
          </h2>
          {modelInfos && modelInfos.length > 0 && (
            <div className="flex items-center gap-1">
              {hasPersonalGroup && (
                <button
                  onClick={() => handleOpenGroupingModal("personal")}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-blue-100 transition"
                  title="Edit My Group"
                >
                  <AccountTreeOutlinedIcon
                    fontSize="small"
                    className="text-blue-600"
                  />
                </button>
              )}
              {hasCommonGroup && (
                <button
                  onClick={() => handleOpenGroupingModal("common")}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-green-100 transition"
                  title="Edit Public Group"
                >
                  <AccountTreeOutlinedIcon
                    fontSize="small"
                    className="text-green-600"
                  />
                </button>
              )}
              <button
                onClick={() => handleOpenGroupingModal(null)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-100 transition"
                title="Group Models"
              >
                <AccountTreeOutlinedIcon
                  fontSize="small"
                  className="text-slate-700"
                />
              </button>
            </div>
          )}
        </div>

        {/* Başlık altı ayırıcı */}
        <div style={{ height:"1px", backgroundColor:"#F1F5F9", margin:"12px 0 16px" }} />

        {/* İçerik */}
        <div className="space-y-3">
          {(!modelInfos || modelInfos.length === 0) && (
            <div className="text-[13px]" style={{ color: "#64748B" }}>No model files yet.</div>
          )}

          {modelInfos?.map((file, index) => (
            <div
              key={index}
              className="rounded-xl p-4" style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0" }}
            >
              {/* Üst: Bilgiler + Thumbnail (sağda) */}
              <div className="flex items-start gap-4">
                <div className="flex-1 space-y-1">
                  {hasAnyThumbnail && (
                    <>
                      <p className="leading-6">
                        <span className={labelCls}>Image:</span>
                      </p>
                      {/* Thumbnail kadar boşluk (56px = h-14) */}
                      <div className="h-6" />
                    </>
                  )}

                  <p className="leading-6">
                    <span className={labelCls}>Name:</span>
                    <span className={valueCls}>{file?.name}</span>
                  </p>
                  <p className="leading-6">
                    <span className={labelCls}>Uploaded By:</span>
                    <span className={valueCls}>{file?.uploadedBy}</span>
                  </p>
                  <p className="leading-6">
                    <span className={labelCls}>Upload Date:</span>
                    <span className={valueCls}>
                      {formatDateFromMillis(file?.uploadDate) ||
                        file?.uploadDate}
                    </span>
                  </p>
                  <p className="leading-6">
                    <span className={labelCls}>Revision Number:</span>
                    <span className={valueCls}>
                      {file?.revisionNumber ?? "1"}
                    </span>
                  </p>
                </div>

                {hasAnyThumbnail && <ThumbnailImage file={file} />}
              </div>
              {/* Ayrıcı */}
              {!isViewer && !isDemo && (
                <div style={{ height:"1px", backgroundColor:"#F1F5F9", margin:"12px 0 6px" }} />
              )}
              {/* Aksiyonlar: sol indir, sağ sil */}
              {!isViewer && !isDemo && (
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handleDownload(file)}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-md  hover:bg-gray-200 transition"
                    title="Download"
                  >
                    <FileDownloadOutlinedIcon
                      fontSize="small"
                      className="text-slate-700"
                    />
                  </button>

                  {canDelete ? (
                    <button
                      onClick={() => confirmDelete(index)}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-gray-200 transition"
                      title="Delete"
                    >
                      <DeleteOutlinedIcon
                        fontSize="small"
                        className="text-slate-700"
                      />
                    </button>
                  ) : (
                    <div className="w-9 h-9" />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {showDeleteModal && (
          <DeleteConfirmationModal
            isOpen={showDeleteModal}
            closeModal={handleCloseModal}
            handleDelete={handleConfirmDelete}
            entityName="this segmented structure"
          />
        )}

        {showGroupingModal && (
          <ModelGroupingModal
            modelInfos={modelInfos}
            existingGroups={getExistingGroups()}
            userRole={userRole}
            editMode={editingGroupType}
            onClose={() => {
              setShowGroupingModal(false);
              setEditingGroupType(null);
            }}
            onSave={handleSaveGroups}
          />
        )}
      </div>
    );
  }

  // ——— WEB (eski tablo görünümü) ———
  return (
    <div className="mb-4 mt-4 font-inter">
      <div className="flex items-center justify-between mb-3">
        <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#0F172A" }}>Model Files</h2>
        {modelInfos && modelInfos.length > 0 && (
          <div className="flex items-center gap-2">
            {hasPersonalGroup && (
              <button
                onClick={() => handleOpenGroupingModal("personal")}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-150"
                style={{ backgroundColor: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.25)", color: "#60A5FA" }}
              >
                <AccountTreeOutlinedIcon fontSize="small" /><span>My Group</span>
              </button>
            )}
            {hasCommonGroup && (
              <button
                onClick={() => handleOpenGroupingModal("common")}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-150"
                style={{ backgroundColor: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.25)", color: "#4ADE80" }}
              >
                <AccountTreeOutlinedIcon fontSize="small" /><span>Public Group</span>
              </button>
            )}
            <button
              onClick={() => handleOpenGroupingModal(null)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-150"
              style={{ backgroundColor: "#F1F5F9", border: "1px solid #E2E8F0", color: "#475569" }}
            >
              <AccountTreeOutlinedIcon fontSize="small" /><span>Group Models</span>
            </button>
          </div>
        )}
      </div>
      <table className="w-full border-collapse text-[15px] font-[600]" style={{ borderRadius: "12px", overflow: "hidden", backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0" }}>
        <thead>
          <tr style={{ backgroundColor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
            {hasAnyThumbnail && (
              <th className="p-3 text-center" style={{ color: "#94A3B8" }}>Image</th>
            )}
            <th className="p-3 text-center" style={{ color: "#94A3B8" }}>Segmented Structure</th>
            <th className="p-3 text-center" style={{ color: "#94A3B8" }}>Uploaded By</th>
            <th className="p-3 text-center" style={{ color: "#94A3B8" }}>Uploaded Date</th>
            <th className="p-3 text-center" style={{ color: "#94A3B8" }}>Revision Number</th>
            {!isViewer && !isDemo && (
              <th className="p-3 text-center" style={{ color: "#94A3B8" }}>Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {modelInfos.map((file, index) => (
            <tr
              key={index}
              style={{
                backgroundColor: index % 2 === 1 ? "#F8FAFC" : "transparent",
                borderBottom: "1px solid #F1F5F9",
                color: "#475569",
              }}
            >
              {hasAnyThumbnail && (
                <td className="text-center"><ThumbnailImage file={file} /></td>
              )}
              <td className="p-3 text-center" style={{ color: "#0F172A" }}>{file.name}</td>
              <td className="p-3 text-center">{file.uploadedBy}</td>
              <td className="p-3 text-center">{formatDateFromMillis(file.uploadDate) || file.uploadDate}</td>
              <td className="p-3 text-center">{file.revisionNumber || "1"}</td>
              {!isViewer && !isDemo && (
                <td className="p-3 text-center">
                  <button
                    className="px-2 py-1 rounded transition-colors duration-150"
                    style={{ color: "#94A3B8" }}
                    onMouseEnter={(e) => e.currentTarget.style.color="#60A5FA"}
                    onMouseLeave={(e) => e.currentTarget.style.color="#94A3B8"}
                    onClick={() => handleDownload(file)}
                  >
                    <FileDownloadOutlinedIcon />
                  </button>
                  <button
                    className="px-2 py-1 rounded ml-2 transition-colors duration-150"
                    style={{ color: "#94A3B8" }}
                    onMouseEnter={(e) => e.currentTarget.style.color="#F87171"}
                    onMouseLeave={(e) => e.currentTarget.style.color="#94A3B8"}
                    onClick={() => { setSelectedFileIndex(index); setShowDeleteModal(true); }}
                  >
                    <DeleteOutlinedIcon />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {showDeleteModal && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          closeModal={handleCloseModal}
          handleDelete={() => {
            if (selectedFileIndex !== null) handleDelete(selectedFileIndex);
            setShowDeleteModal(false);
            setSelectedFileIndex(null);
          }}
          entityName="this segmented structure"
        />
      )}

      {showGroupingModal && (
        <ModelGroupingModal
          modelInfos={modelInfos}
          existingGroups={getExistingGroups()}
          userRole={userRole}
          editMode={editingGroupType}
          onClose={() => {
            setShowGroupingModal(false);
            setEditingGroupType(null);
          }}
          onSave={handleSaveGroups}
        />
      )}
    </div>
  );
};

export default ModelFiles;
